/**
 * Modals for creating business units and systems, shared by the project wizard and the project's
 * Settings → Business units and systems panel.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import type { BusinessUnit, System } from '../api/types';

export function ModalShell({ title, children, onClose }: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-40 bg-ink-900/30 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-md shadow-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-lg font-medium">{title}</h3>
          <button type="button" onClick={onClose} className="text-ink-400 hover:text-ink-700 text-xl leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function CreateBuModal({ companyId, onClose, onCreated }: {
  companyId: string;
  onClose: () => void;
  onCreated: (b: BusinessUnit) => void;
}) {
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const mut = useMutation({
    mutationFn: () => api.post<BusinessUnit>('/business-units', {
      company_id: companyId, name, short_code: shortCode,
    }),
    onSuccess: onCreated,
  });
  const canSubmit = name.trim() !== '' && shortCode.trim() !== '';
  return (
    <ModalShell title="New business unit" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div>
          <label htmlFor="bu-name">Name</label>
          <input id="bu-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full" placeholder="Retail Banking" />
        </div>
        <div>
          <label htmlFor="bu-short">Short code</label>
          <input id="bu-short" value={shortCode} onChange={(e) => setShortCode(e.target.value)} className="w-full" placeholder="retail" />
        </div>
      </div>
      {mut.error && <p className="text-accent text-sm mt-3">{(mut.error as Error).message}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        <button type="button" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()} className="btn-primary">
          {mut.isPending ? 'Creating…' : 'Create BU'}
        </button>
      </div>
    </ModalShell>
  );
}

export function CreateSystemModal({ buId, onClose, onCreated }: {
  buId: string;
  onClose: () => void;
  onCreated: (s: System) => void;
}) {
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [systemType, setSystemType] = useState<System['system_type']>('IT');
  const mut = useMutation({
    mutationFn: () => api.post<System>('/systems', {
      business_unit_id: buId, name, short_code: shortCode, system_type: systemType,
    }),
    onSuccess: onCreated,
  });
  const canSubmit = name.trim() !== '' && shortCode.trim() !== '';
  return (
    <ModalShell title="New system" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div>
          <label htmlFor="sys-name">Name</label>
          <input id="sys-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full" placeholder="Core Banking" />
        </div>
        <div>
          <label htmlFor="sys-short">Short code</label>
          <input id="sys-short" value={shortCode} onChange={(e) => setShortCode(e.target.value)} className="w-full" placeholder="core" />
        </div>
        <div>
          <label htmlFor="sys-type">Type</label>
          <select id="sys-type" value={systemType} onChange={(e) => setSystemType(e.target.value as System['system_type'])} className="w-full">
            <option value="IT">IT</option>
            <option value="OT">OT</option>
            <option value="Product">Product</option>
            <option value="Embedded">Embedded</option>
            <option value="Cloud_service">Cloud service</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      {mut.error && <p className="text-accent text-sm mt-3">{(mut.error as Error).message}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        <button type="button" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()} className="btn-primary">
          {mut.isPending ? 'Creating…' : 'Create system'}
        </button>
      </div>
    </ModalShell>
  );
}
